import log from 'loglevel';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from '@aws-amplify/api-graphql';
import type { S3FileItem, S3ListResult } from '@/types/transferFile';
import { invokeFileManagerLambda } from '@/graphql/mutations';

// Re-export S3FileItem for consumers
export type { S3FileItem };

/**
 * Error class for Lambda operation failures.
 * Preserves the Lambda error type for upstream mapping in TransferFileService.
 */
export class LambdaOperationError extends Error {
  constructor(
    message: string,
    public readonly lambdaErrorType?: string,
    public readonly operation?: string
  ) {
    super(message);
    this.name = 'LambdaOperationError';
  }
}

/**
 * Supported Lambda operations for transfer file management.
 */
type TransferFileOperation =
  'listFiles' | 'downloadFile' | 'deleteFile' | 'uploadFile';

/**
 * Raw file data structure returned by the Lambda listFiles operation.
 */
interface LambdaFileItem {
  readonly key: string;
  readonly name: string;
  readonly size: number;
  readonly lastModified: string;
  readonly isFolder: boolean;
  readonly path: string;
  readonly status?: 'active' | 'processing';
}

/**
 * Response shape from the Lambda listFiles operation.
 */
interface ListFilesResponse {
  readonly files: readonly LambdaFileItem[];
  readonly path: string;
  readonly nextContinuationToken: string | null;
  readonly metadata: {
    readonly totalItems: number;
    readonly folders: number;
    readonly files: number;
    readonly timestamp: string;
  };
}

/**
 * Response shape from the Lambda downloadFile operation.
 */
interface DownloadFileResponse {
  readonly downloadUrl: string;
}

/**
 * Response shape from the Lambda uploadFile operation.
 */
interface UploadFileResponse {
  readonly uploadUrl: string;
  readonly key: string;
  readonly expiresIn: number;
  readonly maxUploadBytes?: number;
  readonly metadata: {
    readonly filePath: string;
    readonly generatedAt: string;
    readonly expiresAt: string;
  };
}

/**
 * Response shape from the Lambda deleteFile operation.
 */
interface DeleteFileResponse {
  readonly message: string;
  readonly archivedKey: string;
  readonly metadata: {
    readonly filePath: string;
    readonly timestamp: string;
  };
}

/** Repository for TransferFile operations via the FileManager Lambda. */
export class TransferFileRepository {
  private readonly client = generateClient();

  constructor() {}

  /**
   * Execute a file manager Lambda operation via GraphQL.
   *
   * @param operation - The Lambda operation to invoke
   * @param params - Operation-specific parameters
   * @returns Parsed response body from the Lambda
   */
  private async executeLambdaOperation<T>(
    operation: TransferFileOperation,
    params: Record<string, unknown> = {}
  ): Promise<T> {
    const response = await this.client.graphql({
      query: invokeFileManagerLambda,
      variables: {
        input: JSON.stringify({
          operation,
          ...params,
        }),
      },
    });

    const graphqlResponse = response as GraphQLResult<{
      invokeFileManagerLambda: string;
    }>;

    if (graphqlResponse.errors && graphqlResponse.errors.length > 0) {
      const errorMessages = graphqlResponse.errors
        .map(err => err.message)
        .join(', ');
      throw new Error(`GraphQL errors: ${errorMessages}`);
    }

    if (!graphqlResponse.data) {
      throw new Error('No data received from GraphQL response');
    }

    const lambdaResponseString = graphqlResponse.data.invokeFileManagerLambda;

    if (!lambdaResponseString) {
      throw new Error(
        'Lambda function returned no response — possible timeout or crash'
      );
    }

    const parsed = JSON.parse(lambdaResponseString);

    // Handle both direct payload (AppSync) and statusCode wrapper (API Gateway) formats
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'statusCode' in parsed &&
      'body' in parsed
    ) {
      if (parsed.statusCode !== 200) {
        const errorBody = this.parseErrorBody(parsed.body);
        throw new LambdaOperationError(
          errorBody?.error?.message ??
            `Lambda operation ${operation} failed with status ${parsed.statusCode}`,
          errorBody?.error?.type,
          operation
        );
      }
      return JSON.parse(parsed.body) as T;
    }

    // Direct payload format (AppSync) — check for error shape
    if (parsed.error) {
      throw new LambdaOperationError(
        parsed.error.message ?? `Lambda operation ${operation} failed`,
        parsed.error.type,
        operation
      );
    }

    return parsed as T;
  }

  /**
   * Safely parse an error response body.
   */
  private parseErrorBody(
    body: string
  ): { error?: { message?: string; type?: string } } | null {
    try {
      return JSON.parse(body);
    } catch {
      return null;
    }
  }

  /**
   * List files and folders at a given path within the active prefix.
   */
  async listFiles(
    relativePath: string,
    options?: { nextToken?: string; pageSize?: number }
  ): Promise<S3ListResult> {
    log.debug('TransferFileRepository: Listing files via Lambda', {
      relativePath,
      options,
    });

    const params: Record<string, unknown> = { path: relativePath };
    if (options?.nextToken) {
      params.continuationToken = options.nextToken;
    }
    if (options?.pageSize) {
      params.maxKeys = options.pageSize;
    }

    const response = await this.executeLambdaOperation<ListFilesResponse>(
      'listFiles',
      params
    );

    // Map Lambda response to S3ListResult
    const files: S3FileItem[] = [];
    const foldersSet = new Set<string>();

    for (const item of response.files) {
      if (item.isFolder) {
        foldersSet.add(item.name);
      } else {
        files.push({
          key: item.key,
          name: item.name,
          size: item.size ?? 0,
          lastModified: new Date(item.lastModified),
          relativePath: relativePath,
          status: item.status,
        });
      }
    }

    return {
      files,
      folders: Array.from(foldersSet).sort(),
      nextToken: response.nextContinuationToken ?? undefined,
    };
  }

  /**
   * Get presigned download URL for a file.
   */
  async getDownloadUrl(key: string): Promise<string> {
    log.debug('TransferFileRepository: Getting download URL via Lambda', {
      key,
    });

    const response = await this.executeLambdaOperation<DownloadFileResponse>(
      'downloadFile',
      { path: key }
    );

    if (!response.downloadUrl || typeof response.downloadUrl !== 'string') {
      throw new Error('Invalid response: missing or invalid downloadUrl');
    }

    return response.downloadUrl;
  }

  /**
   * Upload a file using a presigned PUT URL from the Lambda.
   */
  async uploadFile(
    file: File,
    relativePath: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    log.debug(
      'TransferFileRepository: Starting upload via Lambda presigned URL',
      {
        fileName: file.name,
        fileSize: file.size,
        relativePath,
      }
    );

    // Step 1: Get presigned PUT URL from Lambda
    const response = await this.executeLambdaOperation<UploadFileResponse>(
      'uploadFile',
      {
        path: relativePath,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      }
    );

    if (!response.uploadUrl || typeof response.uploadUrl !== 'string') {
      throw new Error('Invalid response: missing or invalid uploadUrl');
    }

    // Step 2: Upload file directly to S3 via presigned PUT URL
    await this.uploadToPresignedUrl(response.uploadUrl, file, onProgress);

    log.debug('TransferFileRepository: Upload completed successfully', {
      fileName: file.name,
      key: response.key,
    });
  }

  /**
   * Delete a file via the Lambda deleteFile operation.
   */
  async deleteFile(key: string): Promise<void> {
    log.debug('TransferFileRepository: Deleting file via Lambda', { key });

    await this.executeLambdaOperation<DeleteFileResponse>('deleteFile', {
      path: key,
    });

    log.debug('TransferFileRepository: File deleted successfully', { key });
  }

  /** Upload a file to a presigned PUT URL using XMLHttpRequest for progress tracking. */
  private uploadToPresignedUrl(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.setRequestHeader(
        'Content-Type',
        file.type || 'application/octet-stream'
      );

      if (onProgress) {
        xhr.upload.onprogress = (event: ProgressEvent) => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: HTTP ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Upload failed: network error'));
      };

      xhr.send(file);
    });
  }
}
